import { useCallback, useEffect, useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import { Form, FormInstance, Select } from 'antd';
import { AtlasGqlAsset } from 'types/atlas-graphql';
import {
  getAssetColumnCopy,
  getColumnName,
  getOrderedAncestors,
} from 'components/data/assets/assetColumnRenderers';
import {
  AssetOption,
  AssetOptionMeta,
  AssetOptionName,
  StyledSelect,
} from './DamageEditAsset.style';

// maps and sorts assets
const mapAndSortAssets = (assets: AtlasGqlAsset[]) => {
  return sortBy(
    assets.map(asset => {
      const orderedAncestors = getOrderedAncestors(asset).filter(
        ancestor => ancestor?.assetType?.name !== 'Site'
      );
      const parent = orderedAncestors.slice(-1)[0];
      const grandparent = orderedAncestors.slice(-2, -1)[0];

      return {
        id: asset.id as string,
        name: getColumnName(asset),
        assetTypeName: asset?.assetType?.name,
        parent: parent ? getColumnName(parent) : undefined,
        parentTypeName: parent?.assetType?.name,
        grandparent: grandparent ? getColumnName(grandparent) : undefined,
        grandparentTypeName: grandparent?.assetType?.name,
        sortKey: getAssetColumnCopy({ asset, separatorText: ' | ' }),
      };
    }),
    'sortKey'
  );
};

// renders the asset options
const renderAssetOptions = (options: ReturnType<typeof mapAndSortAssets>): JSX.Element[] => {
  return options.map(option => {
    const { id, name, assetTypeName, parent, parentTypeName, grandparent, grandparentTypeName } =
      option;

    const label = assetTypeName ? `${assetTypeName} ${name}` : name;
    const meta = [
      parent && parentTypeName ? `${parentTypeName} ${parent}` : parent,
      grandparent && grandparentTypeName ? `${grandparentTypeName} ${grandparent}` : grandparent,
    ]
      .filter(Boolean)
      .join(' | ');

    return (
      <Select.Option key={id} value={id} label={label}>
        <AssetOption>
          <AssetOptionName>{label}</AssetOptionName>
          {meta && <AssetOptionMeta>{meta}</AssetOptionMeta>}
        </AssetOption>
      </Select.Option>
    );
  });
};

type DamageEditAssetProps = {
  form: FormInstance;
  assetId: string;
  onChange: (assetId: string) => void;
  potentialAssets: {
    likely: AtlasGqlAsset[];
    nearby: AtlasGqlAsset[];
  };
  externalLoading: boolean;
};

export default function DamageEditAsset({
  form,
  assetId,
  onChange,
  potentialAssets,
  externalLoading,
}: DamageEditAssetProps) {
  const [hasUserSelected, setHasUserSelected] = useState<boolean>(false);
  const [internalAssetId, setInternalAssetId] = useState<string>();

  const { likelyAssetOptions, nearbyAssetOptions } = useMemo(() => {
    return {
      likelyAssetOptions: mapAndSortAssets(potentialAssets.likely),
      nearbyAssetOptions: mapAndSortAssets(potentialAssets.nearby),
    };
  }, [potentialAssets]);

  // initialize assetId on mount or when the prop changes
  useEffect(() => {
    // only initialize if user hasn't made a manual selection
    if (!hasUserSelected) {
      const _assetId = assetId ?? likelyAssetOptions[0]?.id ?? nearbyAssetOptions[0]?.id;
      if (_assetId && _assetId !== internalAssetId) {
        setInternalAssetId(_assetId);
        form.setFieldsValue({ assetId: _assetId });
        onChange(_assetId);
      }
    }
  }, [
    form,
    assetId,
    onChange,
    hasUserSelected,
    internalAssetId,
    likelyAssetOptions,
    nearbyAssetOptions,
  ]);

  // handle changes to asset options
  useEffect(() => {
    // skip if user has manually selected an asset -> preserve their choice
    if (hasUserSelected) {
      return;
    }

    // if current asset is not in likely options, update to first available
    const isCurrentAssetInLikely = likelyAssetOptions.some(option => option.id === internalAssetId);
    if (!isCurrentAssetInLikely) {
      const _assetId = likelyAssetOptions[0]?.id ?? nearbyAssetOptions[0]?.id;
      if (_assetId && _assetId !== internalAssetId) {
        setInternalAssetId(_assetId);
        form.setFieldsValue({ assetId: _assetId });
        onChange(_assetId);
      }
    }
  }, [form, onChange, hasUserSelected, internalAssetId, likelyAssetOptions, nearbyAssetOptions]);

  // handle user selection
  const handleAssetChange = useCallback(
    (selectedAssetId: string) => {
      setHasUserSelected(true);
      setInternalAssetId(selectedAssetId);
      onChange(selectedAssetId);
    },
    [onChange]
  );

  return (
    <Form.Item
      label="Damage Asset"
      name="assetId"
      rules={[{ required: true, message: 'Please select an Asset' }]}
    >
      <StyledSelect
        className="testid-damage-asset-edit"
        allowClear={false}
        onChange={handleAssetChange}
        getPopupContainer={triggerNode => triggerNode.parentElement}
        loading={externalLoading}
        optionLabelProp="label"
      >
        <Select.OptGroup key={'likely'} label={'Likely Assets'}>
          {renderAssetOptions(likelyAssetOptions)}
        </Select.OptGroup>
        <Select.OptGroup key={'nearby'} label={'Nearby Assets'}>
          {renderAssetOptions(nearbyAssetOptions)}
        </Select.OptGroup>
      </StyledSelect>
    </Form.Item>
  );
}
